import { GameState, NavigationArrow, ReadingResult } from "@shared/types";
import { GameEvent } from "../enums/GameEvent";
import { Page } from "../enums/Page";

/**
 * Represents the data of a Switch Page event
 */
export type SwitchPageEvent = {
    /** Page to switch to */
    page: Page;
};

/**
 * Represents the data of a Set Room Sound Event event
 */
export type SetRoomSoundEvent = {
    /**
    * Represents the needed sounds to play immediately after (re)loading
    */
    roomSound: string[];
};

/**
 * Represents the data of a Play Sound Event event
 */
export type PlaySoundEvent = {
    /**
    * Represents the name of the sound
    */
    sound: string;
    type?: string;
};

/**
 * Represents the data of an Update Navigation Arrows event
 */
export type UpdateNavigationArrowsEvent = {
    /**
    * Represents a navigation arrow for the client application
    */
    arrows: NavigationArrow[];
};

/**
 * Represents the data of a Switch Room event
 */
export type SwitchRoomEvent = {
    /** Page to switch to */
    state: GameState;
};

/**
 * Represents the data of a Update Read Content event
 */
export type UpdateReadContentEvent = {
    /**
    * Represents the reading result (images to display, object alias and text) for the client application
    */
    readingResult: ReadingResult;
};

/**
 * Represents the data of a Stop Reading event
 */
export type StopReadingEvent = {
    /**
    * Represents the state with the updated text after cross button from read action has been clicked
    */
    state: GameState;
};

export type AchievementNumberEvent = {
    number: number;
};

export type CutsceneEvent = {
    cutsceneNumber: number;
};

export type AnnouncementRequest = {
    sound: string;
};

/**
 * Service to allow components to communicate with eachother through events
 */
export class GameEventService {
    /**
     * Listen for a game event and execute a function when it occurs
     *
     * @template T Type used for the event data
     *
     * @param gameEvent Kind of game event to listen for
     * @param callback Function to call when the game event occurs. Will get the event data as an argument.
     */
    public addGameEventListener<T>(
        gameEvent: GameEvent,
        callback: (event: T) => void
    ): void {
        window.addEventListener(`game:${gameEvent}`, event => {
            callback((event as CustomEvent).detail as T);
        });
    }

    /**
     * Dispatch a Switch Page event
     *
     * @param page Page to switch to
     */
    public switchPage(page: Page): void {
        this.dispatchGameEvent<SwitchPageEvent>(GameEvent.SwitchPage, {
            page: page,
        });
    }

    /**
     * Dispatch a Switch Room Sound event
     *
     * @param roomSound The default music and sounds for the room
     */
    public setRoomSound(roomSound: string[]): void {
        this.dispatchGameEvent<SetRoomSoundEvent>(GameEvent.SetRoomSound, {
            roomSound: roomSound,
        });
    }

    /**
     * Dispatch a Play Sound event
     *
     * @param sound  Name of the sound to play
     */
    public playSound(sound: string, type?: string): void {
        this.dispatchGameEvent<PlaySoundEvent>(GameEvent.PlaySound, {
            sound: sound,
            type: type,
        });
    }

    public achievementNumber(number: number): void {
        this.dispatchGameEvent<AchievementNumberEvent>(GameEvent.AchievementNumber, {
            number: number,
        });
    }

    /**
     * Dispatch a Sound Menu Open event
     *
     * This event notifies the SoundComponent that the sound menu has been opened.
     */
    public SoundMenuOpen(): void {
        this.dispatchGameEvent(GameEvent.SoundMenuOpen);
    }

    /**
     * Dispatch a Stop Audio event
     *
     * This event stops all audio playing in the SoundComponent
     */
    public StopAudio(): void {
        this.dispatchGameEvent(GameEvent.StopAudio);
    }

    /**
     * Dispatch an Update Navigation Arrows event
     *
     * @param arrows The Navigation Arrows used to navigate the room
     */
    public updateNavigationArrows(arrows: NavigationArrow[]): void {
        this.dispatchGameEvent<UpdateNavigationArrowsEvent>(GameEvent.UpdateNavigationArrows, {
            arrows: arrows,
        });
    }

    /**
     * Dispatch a Switch Room event
     *
     * @param state GameState with the new room to switch to
     */
    public switchRoom(state: GameState): void {
        this.dispatchGameEvent<SwitchRoomEvent>(GameEvent.SwitchRoom, {
            state: state,
        });
    }

    /**
     * Dispatch an Update Read Content event
     *
     * @param readingResult Reading Result is the object to display, its images and text to display after stop reading event has been dispatched
     */
    public updateReadContent(readingResult: ReadingResult): void {
        this.dispatchGameEvent<UpdateReadContentEvent>(GameEvent.UpdateReadContent, {
            readingResult: readingResult,
        });
    }

    /**
     * Dispatch a Stop Reading event
     *
     * @param state GameState with the updated text after the stop reading action
     */
    public stopReading(state: GameState): void {
        this.dispatchGameEvent<StopReadingEvent>(GameEvent.StopReading, {
            state: state,
        });
    }

    public startCutscene(cutsceneNumber: number): void {
        this.dispatchGameEvent<CutsceneEvent>(GameEvent.StartCutscene, {
            cutsceneNumber: cutsceneNumber,
        });
    }

    public endCutscene(): void {
        this.dispatchGameEvent(GameEvent.EndCutscene);
    }

    public endCredits(): void {
        this.dispatchGameEvent(GameEvent.EndCredits);
    }

    public newGame(): void {
        this.dispatchGameEvent(GameEvent.NewGameRequest);
    }

    /**
     * Dispatch a game event
     *
     * @template T Type used for the event data
     *
     * @param gameEvent Kind of game event to dispatch
     * @param data Event data to send along with the dispatch
     */
    private dispatchGameEvent<T>(gameEvent: GameEvent, data?: T): void {
        window.dispatchEvent(
            new CustomEvent(`game:${gameEvent}`, {
                detail: data,
            })
        );
    }
}
